import React from 'react';

import styled from '@mint-lib/styled';
import Box from '@mui/material/Box';
import StepM from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepperM, { StepperProps as StepperPropsM } from '@mui/material/Stepper';

import StepContent from './StepContent.jsx';
import { renderDescription } from './Stepper.utils.jsx';

export interface Step {
  title: string;
  description?: string;
  disabled?: boolean;
}
export interface StepperProps extends StepperPropsM {
  activeStep?: number;
  steps: Step[];
  handleStep?: (...args: any) => (e: React.MouseEvent) => void;
  completedStep?: {
    [k: number]: boolean;
  };
  isStepFailed?: (step: number) => boolean;
}

/**
 *
 * @deprecated Use Stepper from @mint-lib/tw-components/patterns instead
 */

const Stepper: React.FC<StepperProps> = ({
  activeStep,
  steps,
  handleStep,
  completedStep,
  isStepFailed,
  ...props
}) => {
  const handleClick = (index: number) => handleStep?.(index);
  return (
    <Box sx={{ width: '100%' }}>
      <StyledStepper activeStep={activeStep} nonLinear {...props}>
        {steps.map((step: Step, index: number) => {
          const labelProps: {
            optional?: React.ReactNode;
            error?: boolean;
          } = {};
          labelProps.optional = renderDescription('uncompleted', step);
          if (activeStep === index) {
            labelProps.optional = renderDescription('active', step);
          }
          if (completedStep && completedStep[index]) {
            labelProps.optional = renderDescription('completed', step);
          }
          if (isStepFailed && isStepFailed(index)) {
            labelProps.error = true;
            labelProps.optional = renderDescription('error', step);
          }
          return (
            <StyledStep
              key={step.title}
              completed={completedStep && completedStep[index]}
              onClick={handleClick(index)}
              disabled={step.disabled}
              handleStep={handleStep}
            >
              <StepLabel {...labelProps} StepIconComponent={StepContent}>
                {step.title}
              </StepLabel>
            </StyledStep>
          );
        })}
      </StyledStepper>
    </Box>
  );
};

export default Stepper;

const StyledStepper = styled(StepperM)(({ theme }) => ({
  '.MuiStepConnector-line': {
    borderColor: theme.palette.border.secondary,
  },
}));
const StyledStep = styled(StepM, {
  shouldForwardProp: (prop) => prop !== 'handleStep',
})<{
  handleStep?: (...args: any) => (e: React.MouseEvent) => void;
  disabled?: boolean;
}>(({ theme, handleStep, disabled }) => ({
  pointerEvents: disabled ? 'none' : 'auto',
  cursor: handleStep ? 'pointer' : 'default',
  '&:first-of-type': {
    paddingLeft: '0px',
  },
  '& .styled-customized-step': {
    backgroundColor: disabled && theme.palette.action.disabledBackground,
  },
  '& .MuiStepLabel-label': {
    ...theme.typography.bodyShort01,
    color: theme.palette.text.primary,
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: `${theme.palette.text.primary} !important`,
    fontWeight: '400',
  },
  '& .MuiStepLabel-label.Mui-error': {
    color: `${theme.palette.text.error} !important`,
  },
  '& .MuiStepLabel-label.Mui-active': {
    fontWeight: '400',
  },
  '& .MuiStepLabel-label.Mui-disabled': {
    color: `${theme.palette.text.disabled} !important`,
  },

  color: disabled ? theme.palette.text.disabled : theme.palette.greyscale.black,
  ' .MuiStepLabel-labelContainer': {
    display: 'flex',
    flexDirection: 'column',
  },
}));
